/**
 * Owner: Masa Bayu
 * File: page.tsx
 * Date:Time: 2026-10-05 14:55
 * Description: Fasa 23 live · table/list structural Face · no Knuth on structure.
 *
 * Note: mounted by RoutePanel at /p23-structural.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { QxEditorPageFaceSpsu } from '../../profile/qx-editor-page-face';
import type { AssetLibrary, SPSUDocument, SPSUPage } from '../../profile/spsu-types';
import '../p02/p02-live.css';

const SPSU_JSON = '/evidence/pkt-ladder/spsu-fasa23-structural-page.json';

type EvidenceFile = {
  readonly spsu: SPSUDocument;
};

async function loadJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
  return (await response.json()) as T;
}

export default function Fasa23StructuralLivePage(): ReactElement {
  const [page, setPage] = useState<SPSUPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const evidence = await loadJson<EvidenceFile>(SPSU_JSON);
        const nextPage = evidence.spsu.pages[0] ?? null;
        if (!nextPage) throw new Error('SPSU evidence has no page');
        if (cancelled) return;
        setPage(nextPage);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="p02-live p02-live-error" role="alert">
        ERROR: {error}
      </div>
    );
  }

  if (!page) {
    return <div className="p02-live p02-live-loading">Loading Fasa 23 structural…</div>;
  }

  const assets: AssetLibrary = {};
  const textCount = page.elements.filter((el) => el.type === 'text').length;

  return (
    <div className="p02-live" data-fasa23-structural-live="">
      <header className="p02-live-header">
        <p className="p02-live-kicker">p23-structural · Fasa 23</p>
        <h1>Fasa 23 · Table & List · No Knuth</h1>
        <p className="p02-live-meta">
          tableLayer + listLayer → .qx-layer-structural · body Knuth terasing · {textCount} teks
        </p>
      </header>
      <section className="p02-live-pane" aria-label="SPSU live face">
        <h2>Live Face · structural + body</h2>
        <div className="p02-live-frame p02-live-frame-face">
          <QxEditorPageFaceSpsu page={page} assets={assets} editable={false} />
        </div>
      </section>
    </div>
  );
}
